// src/components/MsoActivationPanel.tsx
//
// ★★ ANNEX A 22 — the two writes that CHANGE WHAT A BILL IS PRICED AT: activating a draft and
// retiring a rule. Both go through `ConfirmWrite`; neither acts on one click.
//
// ══ ACTIVATE: THE REASONS ARE SHOWN BEFORE THE REFUSAL, NOT AFTER ══
//
// The route refuses an activation that has no passing test run on the CURRENT definition
// (RULE_NOT_TESTED) and one made by the officer who created or last edited the draft
// (MAKER_CHECKER_VIOLATION), and the database enforces the second too. This panel computes the same
// two conditions (`lib/mso-editor.ts`) so the button is disabled WITH ITS REASON on screen, instead
// of the officer pressing it to be told. Both reasons are listed when both apply: a single reason
// would send an officer round the loop twice.
//
// ⚠ The button being disabled is presentation, never enforcement; the route refuses regardless.
//
// ⚠ **The confirmation sentence is frozen when the button is pressed**, naming the rule, the version,
// the date it takes effect and what it supersedes, because that sentence is what the officer is
// agreeing to. The provenance note they typed is stored with the version and printed beside it.
import { plainProse } from '../lib/plain-words'
import { useEffect, useId, useRef, useState } from 'react'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { formatIsoDate } from '../lib/format'
import { activationBlockers } from '../lib/mso-editor'
import { ConfirmWrite } from './ConfirmWrite'
import { DatePicker } from './DatePicker'
import { FormResult, type FormResultState } from './FormResult'
import type { Viewer } from '../lib/mso-editor'
import type { MsoActivationWarning, MsoFieldBounds, MsoRuleVersion, MsoTestRun } from '../lib/types'

/** At most this many warnings are listed; the rest are counted, not hidden. */
const MAX_WARNINGS_SHOWN = 10

/**
 * ★ Things worth checking BEFORE a draft is activated (for example another rule in force that claims the same bills at
 * the same precedence). Advice, never a gate: the Activate button does not read this list, and the words say so.
 * Rendered as a named region directly above the button, so it is read at the moment of the decision.
 */
export function ActivationWarnings({ warnings }: { warnings: readonly MsoActivationWarning[] }) {
  const headingId = useId()
  if (warnings.length === 0) return null
  const shown = warnings.slice(0, MAX_WARNINGS_SHOWN)
  const hidden = warnings.length - shown.length
  return (
    <section role="region" aria-labelledby={headingId} className="pt-well p-3 text-sm" data-testid="activation-warnings">
      <h3 id={headingId} className="text-base font-semibold m-0 mb-1">
        Check before you activate ({warnings.length})
      </h3>
      <p className="m-0 mb-2 text-[var(--sgds-body-color-subtle)]">These are worth a look. None of them stops you from activating.</p>
      <ul className="list-disc pl-5 m-0 flex flex-col gap-1">
        {shown.map((warning, index) => (
          <li key={`${warning.kind}-${index}`} data-warning={warning.kind}>
            {plainProse(warning.message)}
          </li>
        ))}
      </ul>
      {hidden > 0 && <p className="m-0 mt-2">{hidden} more {hidden === 1 ? 'check is' : 'checks are'} not listed here.</p>}
    </section>
  )
}

export interface MsoActivationPanelProps {
  readonly version: MsoRuleVersion
  /** The signed-in officer, as far as the author check can tell (see `viewerAuthoredDraft`). */
  readonly viewer: Viewer | null
  readonly testRuns: readonly MsoTestRun[]
  readonly dirty: boolean
  readonly onChanged: () => void
  /**
   * Where the success sentence goes once the write is done. The page passes one that survives the re-render a
   * write causes and moves keyboard focus to the message; without it the panel shows the message itself.
   */
  readonly onNotice?: ((message: string) => void) | undefined
  /** The server's pre-activation advice (empty unless this is a draft). */
  readonly warnings?: readonly MsoActivationWarning[] | undefined
  /** Who is blocked from activating this draft (server wording). Optional: an older server sends nothing. */
  readonly makerCheckerNote?: string | null | undefined
  /** Whether the signed-in officer may activate it. Optional for the same reason. */
  readonly youCanActivate?: boolean | null | undefined
  /** The server's published bound for the provenance note. */
  readonly bounds?: MsoFieldBounds | undefined
}

export function MsoActivationPanel({ version, viewer, testRuns, dirty, onChanged, onNotice, warnings = [], bounds, makerCheckerNote = null, youCanActivate = null }: MsoActivationPanelProps) {
  const baseId = useId()
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [state, setState] = useState<FormResultState>({ status: 'idle' })

  const blockers = activationBlockers({ version, viewer, runs: testRuns, dirty, note })

  const performActivate = async () => {
    setConfirming(null)
    setState({ status: 'submitting' })
    try {
      await api.activateMsoDraft(version.id, note.trim())
      const message = `${version.ruleKey} version ${version.version} is now active from ${formatIsoDate(version.effectiveFrom)}.`
      if (onNotice === undefined) setState({ status: 'success', message })
      else {
        setState({ status: 'idle' })
        onNotice(message)
      }
      onChanged()
    } catch (error) {
      setState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not activate this draft.' })
    }
  }

  return (
    <section aria-label="Activation" className="pt-card flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Activate</h2>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          Activating puts this version into force: from {formatIsoDate(version.effectiveFrom)}, every MSO bill it claims is
          priced by it, and the version in force is superseded. It needs a passing test run on this exact definition, and a
          second PSD officer to press it.
        </p>
      </div>

      <div className="flex flex-col gap-1 text-sm">
        <label htmlFor={`${baseId}-note`} className="font-semibold">
          Where this rule&rsquo;s figures came from <span aria-hidden="true">*</span>
          <span className="sr-only">required</span>
        </label>
        <textarea
          id={`${baseId}-note`}
          className="pt-native-date"
          rows={3}
          value={note}
          {...(bounds !== undefined && { maxLength: bounds.note })}
          placeholder="The circular, the policy paper or the PSD decision these figures come from"
          onChange={(e) => setNote(e.target.value)}
          aria-describedby={`${baseId}-count`}
        />
        <span id={`${baseId}-count`} className="text-xs text-[var(--sgds-body-color-subtle)]">
          {bounds === undefined ? `${note.trim().length} characters.` : `${bounds.note - note.length} of ${bounds.note} characters left.`} Stored with the version and printed beside it.
        </span>
      </div>

      {blockers.length > 0 && (
        <div role="status" className="pt-well p-3 text-sm" data-testid="activation-blockers">
          <p className="font-semibold m-0 mb-1">Activate is unavailable for now:</p>
          <ul className="list-disc pl-5 m-0 flex flex-col gap-1">
            {blockers.map((b) => (
              <li key={b.code} data-blocker={b.code}>
                {plainProse(b.reason)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {makerCheckerNote !== null && makerCheckerNote.length > 0 && (
        <div role="status" className="pt-well p-3 text-sm" data-testid="activation-who">
          <p className="font-semibold m-0 mb-1">{youCanActivate === false ? 'You cannot activate this draft' : 'Who can activate this draft'}</p>
          <p className="m-0">{plainProse(makerCheckerNote)}</p>
          <p className="m-0 mt-1">
            An officer who created or last edited a draft cannot activate it, and that applies to every officer named above. A
            different PSD officer, who did not create or edit this draft, is needed to put it into force.
          </p>
        </div>
      )}

      {confirming !== null && (
        <ConfirmWrite
          summary={confirming}
          confirmLabel="Yes, activate it"
          variant="danger"
          busy={state.status === 'submitting'}
          onConfirm={() => void performActivate()}
          onCancel={() => setConfirming(null)}
        />
      )}

      <FormResult state={state} />

      <ActivationWarnings warnings={warnings} />

      <div>
        <SgdsButton
          type="button"
          variant="primary"
          disabled={blockers.length > 0 || state.status === 'submitting'}
          onClick={() =>
            setConfirming(
              `Activate ${version.ruleKey} version ${version.version} from ${formatIsoDate(version.effectiveFrom)}. From that date it prices every MSO bill it claims, and the version in force is superseded. There is no undo: a mistake is corrected by another version.`,
            )
          }
        >
          Activate
        </SgdsButton>
      </div>
    </section>
  )
}

export function MsoRetirePanel({
  version,
  onChanged,
  onNotice,
  bounds,
}: {
  version: MsoRuleVersion
  onChanged: () => void
  onNotice?: ((message: string) => void) | undefined
  bounds?: MsoFieldBounds | undefined
}) {
  const baseId = useId()
  const [until, setUntil] = useState('')
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [state, setState] = useState<FormResultState>({ status: 'idle' })
  const canRetire = until.length > 0 && note.trim().length > 0
  // N-03: the stop date is checked BEFORE the question is asked. The engine's end date is exclusive, so a date on or before
  // the day this version starts would leave it applying to no day at all.
  const [dateRefusal, setDateRefusal] = useState<string | null>(null)
  const dateRefusalRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (dateRefusal !== null) dateRefusalRef.current?.focus()
  }, [dateRefusal])

  const performRetire = async () => {
    setConfirming(null)
    setState({ status: 'submitting' })
    try {
      await api.retireMsoRule(version.ruleKey, { effectiveUntil: until, note: note.trim() })
      const message = `${version.ruleKey} is retired. It stops applying on ${formatIsoDate(until)}.`
      if (onNotice === undefined) setState({ status: 'success', message })
      else {
        setState({ status: 'idle' })
        onNotice(message)
      }
      onChanged()
    } catch (error) {
      setState({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not retire this rule.' })
    }
  }

  return (
    // A disclosure, closed until an officer opens it: an open red Retire button on an in-force Authority rule is a
    // foot-gun in a demonstration (UI review, 2026-10-14). Everything inside stays in the DOM, so it is reachable by
    // keyboard the moment it is opened.
    <details aria-label="Retire" className="pt-card">
      <summary className="cursor-pointer text-lg font-semibold">Retire this rule</summary>
      <div className="flex flex-col gap-4 mt-3">
      <div>
        <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[75ch] m-0">
          Retiring ends the rule on a date. A bill dated on or after that date is no longer priced by this rule, and is
          refused if no other rule claims it. Calculations already recorded keep naming the version that priced them.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1 text-sm">
          <span id={`${baseId}-until`} className="font-semibold">
            Stops applying on <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </span>
          <DatePicker value={until} onChange={(v) => { setUntil(v); setDateRefusal(null) }} labelledBy={`${baseId}-until`} required subject="stop date" />
          {dateRefusal !== null && (
            <p ref={dateRefusalRef} tabIndex={-1} role="alert" className="text-sm m-0 text-[var(--sgds-danger-color-emphasis)]" data-testid="retire-date-refusal">
              {dateRefusal}
            </p>
          )}
          <span className="text-xs text-[var(--sgds-body-color-subtle)]">A bill dated on or after this date is no longer priced by this rule.</span>
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor={`${baseId}-note`} className="font-semibold">
            Reason <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </label>
          <textarea
            id={`${baseId}-note`}
            className="pt-native-date"
            rows={2}
            value={note}
            {...(bounds !== undefined && { maxLength: bounds.note })}
            placeholder="Why this rule is ending"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      {confirming !== null && (
        <ConfirmWrite
          summary={confirming}
          confirmLabel="Yes, retire it"
          variant="danger"
          busy={state.status === 'submitting'}
          onConfirm={() => void performRetire()}
          onCancel={() => setConfirming(null)}
        />
      )}
      <FormResult state={state} />
      <div>
        <SgdsButton
          type="button"
          variant="danger"
          disabled={!canRetire || state.status === 'submitting'}
          onClick={() => {
            if (until <= version.effectiveFrom) {
              setDateRefusal(`Choose a date after ${formatIsoDate(version.effectiveFrom)}, the day this version starts.`)
              return
            }
            setDateRefusal(null)
            setConfirming(
              `Retire ${version.ruleKey} so that it stops applying on ${formatIsoDate(until)}. A bill dated on or after that date is no longer priced by this rule. There is no undo: a mistake is corrected by another version.`,
            )
          }}
        >
          Retire
        </SgdsButton>
      </div>
      </div>
    </details>
  )
}
